// ── agentHelpers.js ─────────────────────────────────────────────────────────
//
// Helper functions for ProductDataAgent.
//
// IMPORTANT: STAGING_COLUMNS order is fixed and must match the Make.com field
// mapping. If you reorder columns, update the Make scenario Add a Row module
// to match before deploying.
//
// Write rules (non-negotiable):
//   • Approved rows append to agent_staging ONLY
//   • Never write to products_master or any other tab
//   • Only permitted operation: appendRow (via webhook POST)
//
// ────────────────────────────────────────────────────────────────────────────

// 24-column staging row — zero-indexed, matches Make.com Add a Row module.
// NOTE: Date_checked is auto-generated by buildStagingRow at submit time.
export const STAGING_COLUMNS = [
  'productId',          // row[0]  — snake_case family slug e.g. salted_butter_250g
  'Product_name_app',   // row[1]  — clean display name e.g. "Salted Butter"
  'category',           // row[2]  — snake_case aisle e.g. fresh_fridge
  'subcategory',        // row[3]  — optional, more specific e.g. butter
  'standardizedSize',   // row[4]  — canonical family size constant
  'unitType',           // row[5]  — g | kg | ml | L | each | pack
  'Product_name_store', // row[6]  — store's exact product title
  'variant',            // row[7]  — OPTIONAL: salted | unsalted | 0_percent etc.
  'storeSize',          // row[8]  — plain number, no units
  'barcode',            // row[9]  — OPTIONAL: EAN/UPC if found
  'storeProductId',     // row[10] — extracted from URL
  'OwnBrand',           // row[11] — true | false
  'tier',               // row[12] — budget | standard | premium | organic
  'Store',              // row[13] — Tesco | Sainsburys | Aldi | Lidl
  'Price',              // row[14] — standard shelf price in £ e.g. 2.15
  'pricePerUnit',       // row[15] — float to 2dp e.g. 0.86
  'promoPrice',         // row[16] — OPTIONAL: promo/Clubcard price if present
  'promoType',          // row[17] — OPTIONAL: Clubcard | Nectar | multibuy
  'promoEndDate',       // row[18] — OPTIONAL: ISO date if known
  'LastPriceChange',    // row[19] — OPTIONAL: ISO date if found on page
  'imageUrl',           // row[20] — OPTIONAL: product image URL
  'Link',               // row[21] — canonical URL, no tracking params
  'Date_checked',       // row[22] — auto-generated at submit time (YYYY-MM-DD)
  'confidence',         // row[23] — high | medium | low
];

// Fields that must be OMITTED rather than set to null/"" when not applicable.
// (Omit-not-null rule from Product Mapping Rules v1.4)
export const OPTIONAL_COLS = new Set([
  'subcategory', 'variant', 'barcode',
  'promoPrice', 'promoType', 'promoEndDate', 'LastPriceChange',
]);

// ── Validation constants ──────────────────────────────────────────────────────

export const REQUIRED_FIELDS = [
  'productId', 'Product_name_app', 'category', 'standardizedSize',
  'unitType', 'Product_name_store', 'storeSize', 'OwnBrand',
  'tier', 'Store', 'Price', 'pricePerUnit', 'confidence',
];

export const ALLOWED_CATEGORIES = [
  'fruit_veg', 'fresh_fridge', 'bakery', 'tinned_cupboard',
  'snacks', 'drinks', 'frozen', 'meat_poultry', 'household',
];

export const ALLOWED_STORES = ['Tesco', 'Sainsburys', 'Aldi', 'Lidl'];

// Enum-locked: any other value must fail validation immediately (not just warn).
export const ALLOWED_TIERS = ['budget', 'standard', 'premium', 'organic'];

// ── Tier keyword map ──────────────────────────────────────────────────────────
//
// Deterministic brand/range → tier lookup.
// mapTierFromEvidence() uses this — AI is NOT trusted to assign tier.
//
// Structure: { [Store]: { [tier]: string[] } }
// Each entry is a lowercase substring to match against brandRange + productTitle.

const TIER_KEYWORDS = {
  Tesco: {
    organic:  ['organic'],
    premium:  ['finest'],
    budget:   ["h.w.nevill's", 'everyday value', 'tesco value', 'value'],
    // standard = fallback (no matching keywords)
  },
  Sainsburys: {
    organic:  ['organic', 'so organic'],
    premium:  ['taste the difference'],
    budget:   ['stamford street', 'basics'],
    // standard = fallback
  },
  Aldi: {
    organic:  ['organic'],
    premium:  ['specially selected'],
    budget:   ['everyday essentials'],
    // standard = fallback (unbranded Aldi own-label)
  },
  Lidl: {
    organic:  ['organic', 'bio'],
    premium:  ['deluxe'],
    budget:   [],  // base Lidl unbranded = budget
    // standard = mid-tier Lidl branded ranges
  },
};

// ── mapTierFromEvidence ───────────────────────────────────────────────────────
//
// Deterministic code-based tier mapping.
// Called by parseResearchResponse to OVERRIDE AI-assigned tier.
// Returns the mapped tier string, or null if the store is unknown (keep AI value).

export function mapTierFromEvidence(store, brandRange, productTitle) {
  const map = TIER_KEYWORDS[store];
  if (!map) return null; // unknown store — cannot map, keep AI value

  const text = `${brandRange || ''} ${productTitle || ''}`.toLowerCase();

  // Check in priority order: organic > premium > budget > standard
  for (const tier of ['organic', 'premium', 'budget']) {
    if ((map[tier] || []).some(kw => text.includes(kw))) return tier;
  }

  // Lidl unbranded base (no premium/organic keyword found) → budget
  if (store === 'Lidl' && !text.includes('deluxe') && !text.includes('organic') && !text.includes('bio')) {
    return 'budget';
  }

  return 'standard';
}

// ── Price sanity ranges by product family ─────────────────────────────────────
//
// Maps productId to expected price range in £.
// Used by validateCandidate for family-specific sanity checks.
// Keep this list growing as product families are added to the catalogue.

export const PRICE_SANITY_RANGES = {
  salted_butter_250g:       { min: 0.90, max: 3.50 },
  unsalted_butter_250g:     { min: 0.90, max: 3.50 },
  semi_skimmed_milk_2l:     { min: 0.90, max: 2.50 },
  semi_skimmed_milk_4pt:    { min: 0.90, max: 2.50 },
  whole_milk_2l:            { min: 0.90, max: 2.50 },
  skimmed_milk_2l:          { min: 0.90, max: 2.50 },
  baked_beans_400g:         { min: 0.20, max: 1.50 },
  cheddar_mild_400g:        { min: 1.50, max: 5.00 },
  cheddar_mature_400g:      { min: 1.50, max: 5.00 },
  cheddar_extra_mature_400g:{ min: 1.50, max: 5.50 },
  greek_yogurt_500g:        { min: 0.80, max: 4.50 },
  plain_yogurt_500g:        { min: 0.60, max: 3.00 },
  olive_oil_500ml:          { min: 1.50, max: 8.00 },
  olive_oil_750ml:          { min: 2.00, max: 10.00 },
  white_bread_800g:         { min: 0.45, max: 3.00 },
  eggs_12:                  { min: 1.50, max: 5.00 },
  eggs_6:                   { min: 0.90, max: 3.00 },
};

// ── validateCandidate ─────────────────────────────────────────────────────────
//
// Validates a fields object against Product Mapping Rules v1.4.
// Returns { errors: string[], warnings: string[] }
//   errors   — block approval (required fields missing, invalid values,
//               inferred price, inferred tier)
//   warnings — flag for review but do not block approval

export function validateCandidate(fields) {
  const errors   = [];
  const warnings = [];

  // 1. Required fields
  const missing = REQUIRED_FIELDS.filter(f => {
    const v = fields[f];
    return v === undefined || v === null || v === '';
  });
  if (missing.length > 0) {
    errors.push(`Missing required fields: ${missing.join(', ')}`);
  }

  // 2. Category must be in approved list
  if (fields.category && !ALLOWED_CATEGORIES.includes(fields.category)) {
    errors.push(`Invalid category "${fields.category}" — must be one of: ${ALLOWED_CATEGORIES.join(', ')}`);
  }

  // 3. Store must be in approved list
  if (fields.Store && !ALLOWED_STORES.includes(fields.Store)) {
    errors.push(`Invalid store "${fields.Store}" — must be one of: ${ALLOWED_STORES.join(', ')}`);
  }

  // 4. Tier must be in approved list (enum-locked — any other value blocks approval)
  if (fields.tier && !ALLOWED_TIERS.includes(fields.tier)) {
    errors.push(`Invalid tier "${fields.tier}" — must be budget | standard | premium | organic`);
  }

  // 5. Variant only allowed for specific product types
  if (fields.variant) {
    const allowed = ['salted', 'unsalted', '0_percent', '5_percent', 'full_fat',
                     'mild', 'medium', 'mature', 'extra_mature', 'vintage'];
    if (!allowed.includes(fields.variant)) {
      errors.push(`Invalid variant "${fields.variant}" — not in Phase 1 dictionary`);
    }
  }

  // 6. Price sanity — generic range
  const price = Number(fields.Price);
  if (fields.Price !== undefined && fields.Price !== null && fields.Price !== '') {
    if (price < 0.01 || price > 200) {
      warnings.push(`Unusual price £${price} — verify before approving`);
    }

    // 6b. Product-family specific range check
    const range = PRICE_SANITY_RANGES[fields.productId];
    if (range && (price < range.min || price > range.max)) {
      warnings.push(
        `Price £${price.toFixed(2)} is outside expected range £${range.min.toFixed(2)}–${range.max.toFixed(2)} for ${fields.productId} — verify against store website`
      );
    }
  }

  // 7. Price source — inferred price BLOCKS approval
  if (fields.priceSource === 'inferred') {
    errors.push('Price is AI-inferred (no real product page found) — approval blocked. Verify price on the store website first.');
  }

  // 8. Price source — knowledge-based price warns (training data, not live)
  if (fields.priceSource === 'knowledge') {
    warnings.push('Price is from AI training knowledge, not a live product page — verify current price on store website before approving');
  }

  // 9. No source URL
  if (!fields.Link) {
    warnings.push('No source URL — verify on store website before approving');
  }

  // 10. Low confidence
  if (fields.confidence === 'low') {
    warnings.push('Low confidence — agent was uncertain about one or more fields');
  }

  return { errors, warnings };
}

// ── normaliseUrl ─────────────────────────────────────────────────────────────
//
// Strip tracking parameters from a store product URL, detect the store from
// the hostname, and extract storeProductId from the path.
//
// Returns { cleanUrl, store, storeProductId } — any field may be null if it
// cannot be determined.

export function normaliseUrl(rawUrl) {
  let url;
  try {
    url = new URL((rawUrl || '').trim());
  } catch {
    return { cleanUrl: (rawUrl || '').trim(), store: null, storeProductId: null };
  }

  // Drop all query parameters and fragment
  url.search = '';
  url.hash   = '';
  const cleanUrl  = url.toString();
  const host      = url.hostname.toLowerCase();
  const pathParts = url.pathname.split('/').filter(Boolean);

  let store          = null;
  let storeProductId = null;

  if (host.includes('tesco.com')) {
    store = 'tesco';
    // /groceries/en-GB/products/290920510
    const idx = pathParts.indexOf('products');
    if (idx !== -1 && pathParts[idx + 1]) {
      storeProductId = pathParts[idx + 1];
    }
  } else if (host.includes('sainsburys.co.uk')) {
    store = 'sainsburys';
    // Last segment often ends with a numeric ID: something_12345678
    const last     = pathParts[pathParts.length - 1] || '';
    const numMatch = last.match(/(\d{6,})/);
    if (numMatch) storeProductId = numMatch[1];
  } else if (host.includes('aldi.co.uk')) {
    store = 'aldi';
    // /p/product-name/123456 — numeric last segment
    const last     = pathParts[pathParts.length - 1] || '';
    const numMatch = last.match(/(\d{4,})/);
    storeProductId = numMatch ? numMatch[1] : (last || null);
  } else if (host.includes('lidl.co.uk')) {
    store = 'lidl';
    storeProductId = pathParts[pathParts.length - 1] || null;
  }

  return { cleanUrl, store, storeProductId };
}

// ── parseResearchResponse ────────────────────────────────────────────────────
//
// Parse a multi-candidate research response from the agent.
// Applies mapTierFromEvidence() to OVERRIDE AI-assigned tier with
// deterministic code-based mapping.
// Returns { candidates: ReviewItem[], agentLog: string[] }

function normaliseStatus(raw) {
  const s = (raw || '').toUpperCase().trim();
  const map = { APPROVE: 'READY', REVIEW: 'NEEDS_REVIEW', REJECT: 'REJECT' };
  return map[s] || s || 'NEEDS_REVIEW';
}

let _idSeq = 0;
export function parseResearchResponse(text) {
  const blockMatch = text.match(/```json\s*([\s\S]*?)```/i);
  const rawJson    = blockMatch ? blockMatch[1] : text.match(/\{[\s\S]*\}/)?.[0];

  if (!rawJson) throw new Error('No JSON block found in agent response.');

  let parsed;
  try { parsed = JSON.parse(rawJson); }
  catch (e) { throw new Error('Agent response contained invalid JSON: ' + e.message); }

  if (!Array.isArray(parsed.candidates)) {
    throw new Error('Agent response missing candidates array.');
  }

  const candidates = parsed.candidates.map(c => {
    const evidence = c.evidence || null;
    const fields   = { ...(c.fields || {}) };

    // ── Apply deterministic tier mapping (overrides AI-assigned tier) ──
    if (evidence) {
      const codeTier = mapTierFromEvidence(
        fields.Store,
        evidence.brandRange || '',
        evidence.productTitle || fields.Product_name_store || ''
      );
      if (codeTier !== null) fields.tier = codeTier;

      // Propagate priceSource from evidence into fields for validateCandidate
      if (evidence.priceSource) fields.priceSource = evidence.priceSource;
    }

    return {
      id:        `item-${Date.now()}-${++_idSeq}`,
      status:    normaliseStatus(c.status || c.decision),
      evidence,
      fields,
      reasoning: c.reasoning || '',
      flags:     Array.isArray(c.flags)   ? c.flags   : [],
      missing:   Array.isArray(c.missing) ? c.missing : [],
    };
  });

  return {
    candidates,
    agentLog: Array.isArray(parsed.agentLog) ? parsed.agentLog : ['Research complete.'],
  };
}

// ── parseAgentResponse ───────────────────────────────────────────────────────
//
// Extract the structured staging result from a raw Anthropic API response.
// Returns { status, fields, reasoning, flags, missing } or throws on malformed output.
//
// status: READY | NEEDS_REVIEW | REJECT

export function parseAgentResponse(text) {
  // Try to find a ```json ... ``` block first, then fall back to bare JSON object
  const blockMatch = text.match(/```json\s*([\s\S]*?)```/i);
  const rawJson    = blockMatch ? blockMatch[1] : text.match(/\{[\s\S]*\}/)?.[0];

  if (!rawJson) {
    throw new Error('No JSON block found in agent response.');
  }

  let parsed;
  try {
    parsed = JSON.parse(rawJson);
  } catch (e) {
    throw new Error('Agent response contained invalid JSON: ' + e.message);
  }

  // Accept 'status' (new) or 'decision' (legacy), normalise to new values
  const raw = (parsed.status || parsed.decision || '').toUpperCase().trim();
  const STATUS_MAP = { APPROVE: 'READY', REVIEW: 'NEEDS_REVIEW', REJECT: 'REJECT' };
  const status = STATUS_MAP[raw] || raw;

  if (!['READY', 'NEEDS_REVIEW', 'REJECT'].includes(status)) {
    throw new Error(`Unexpected status value: "${parsed.status || parsed.decision}"`);
  }

  return {
    status,
    fields:    parsed.fields    || {},
    reasoning: parsed.reasoning || '',
    flags:     Array.isArray(parsed.flags)   ? parsed.flags   : [],
    missing:   Array.isArray(parsed.missing) ? parsed.missing : [],
  };
}

// ── buildStagingRow ──────────────────────────────────────────────────────────
//
// Build the fixed 24-element array from agent-extracted fields.
// Date_checked is auto-generated at submit time.
// Optional fields absent from `fields` are stored as '' for sheet compatibility.

export function buildStagingRow(fields) {
  const dateChecked = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  return STAGING_COLUMNS.map(col => {
    if (col === 'Date_checked') return dateChecked;

    const val = fields[col];

    // Omit-not-null: optional absent fields → empty string for sheet compatibility
    if (val === undefined || val === null || val === '') return '';

    return val;
  });
}

// ── sendApprovedRow ──────────────────────────────────────────────────────────
//
// POST an approved 24-element row to the Make webhook.
// target is always "agent_staging" — a safety label for the Make scenario.
// Throws on network failure or non-2xx HTTP status.
//
// NOTE: The Make.com scenario field mapping must match the new 24-column schema.

export async function sendApprovedRow(row) {
  const webhookUrl = process.env.REACT_APP_MAKE_WEBHOOK_URL;
  if (!webhookUrl) {
    throw new Error('REACT_APP_MAKE_WEBHOOK_URL is not set. Add it to .env and restart.');
  }

  const payload = { target: 'agent_staging', row };

  const response = await fetch(webhookUrl, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Webhook returned ${response.status} ${response.statusText}`);
  }

  return { ok: true };
}
